import { type PermitRow, customerTags } from '@mollusk/shared';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useMemo, useState } from 'react';
import { NonBuildingBadge } from '../../components/NonBuildingBadge';
import { TagChip } from '../../components/TagChip';
import { MONO } from '../../theme';
import { useIsAdmin } from '../auth/internal';
import { PermitDetailModal } from './PermitDetailModal';
import {
  type PermitFilters,
  type PermitSortKey,
  STATUS_DATE_HELP,
  STATUS_DATE_LABEL,
  filterPermits,
  isStatusDated,
  permitDate,
  permitFacets,
  scopeLabel,
  sortPermits,
  typeLabel,
} from './permitView';

const COLUMNS: { key: PermitSortKey | null; label: string }[] = [
  { key: 'date', label: 'Date' },
  { key: 'number', label: 'Permit #' },
  { key: 'type', label: 'Type' },
  { key: 'description', label: 'Description' },
  { key: null, label: 'Tags' },
  { key: 'scope', label: 'Scope' },
  { key: 'final', label: 'Final date' },
];
const PER_PAGE = 25;

function FilterSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (v: string) => void;
}) {
  return (
    <TextField
      select
      size="small"
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      sx={{ minWidth: 150 }}
    >
      <MenuItem value="">All</MenuItem>
      {options.map((o) => (
        <MenuItem key={o} value={o}>
          {o}
        </MenuItem>
      ))}
    </TextField>
  );
}

/**
 * Permits at an address (#171): newest first, paged, sortable, and filterable
 * by tag, type and year. Click a row for the permit popup.
 */
const MAX_TAG_CHIPS = 3;

/** "+N more" after the visible tags; opens the detail modal, which lists them all. */
function MoreTagsChip({ hidden, onOpen }: { hidden: string[]; onOpen: () => void }) {
  return (
    <Chip
      size="small"
      variant="outlined"
      clickable
      label={`+${hidden.length} more`}
      title={hidden.join(', ')}
      aria-label={`${hidden.length} more tags: ${hidden.join(', ')}`}
      onClick={(e) => {
        e.stopPropagation();
        onOpen();
      }}
      sx={{ fontSize: 12, height: 22 }}
    />
  );
}

export function PermitsTable({
  permits,
  showAddress = false,
}: {
  permits: PermitRow[];
  /** Search results span addresses; show where each permit is. */
  showAddress?: boolean;
}) {
  const [active, setActive] = useState<PermitRow | null>(null);
  const [filters, setFilters] = useState<PermitFilters>({});
  const [sortKey, setSortKey] = useState<PermitSortKey>('date');
  const [dir, setDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const isAdmin = useIsAdmin();
  const facets = useMemo(() => permitFacets(permits), [permits]);
  const shown = useMemo(
    () => sortPermits(filterPermits(permits, filters), sortKey, dir),
    [permits, filters, sortKey, dir],
  );

  if (permits.length === 0) {
    return (
      <Typography>
        {showAddress ? 'No matching permits.' : 'No permits found at this address.'}
      </Typography>
    );
  }

  const setFilter = (k: keyof PermitFilters) => (v: string) => {
    setFilters({ ...filters, [k]: v || undefined });
    setPage(0);
  };
  const onSort = (key: PermitSortKey) => {
    if (key === sortKey) setDir(dir === 'asc' ? 'desc' : 'asc');
    else {
      setSortKey(key);
      setDir(key === 'date' || key === 'final' ? 'desc' : 'asc');
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <FilterSelect
          label="Tag"
          value={filters.tag ?? ''}
          options={facets.tags}
          onChange={setFilter('tag')}
        />
        <FilterSelect
          label="Type"
          value={filters.type ?? ''}
          options={facets.types}
          onChange={setFilter('type')}
        />
        <FilterSelect
          label="Year"
          value={filters.year ?? ''}
          options={facets.years}
          onChange={setFilter('year')}
        />
      </Box>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {COLUMNS.map((c) => (
                <TableCell key={c.label} sx={{ whiteSpace: 'nowrap' }}>
                  {c.key ? (
                    <TableSortLabel
                      active={sortKey === c.key}
                      direction={sortKey === c.key ? dir : 'asc'}
                      onClick={() => c.key && onSort(c.key)}
                    >
                      {c.label}
                    </TableSortLabel>
                  ) : (
                    c.label
                  )}
                </TableCell>
              ))}
              {showAddress ? <TableCell>Address</TableCell> : null}
              {isAdmin ? <TableCell>Source</TableCell> : null}
            </TableRow>
          </TableHead>
          <TableBody>
            {shown.slice(page * PER_PAGE, (page + 1) * PER_PAGE).map((p) => (
              <TableRow
                key={`${p.source_id ?? ''}:${p.permit_id}`}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => setActive(p)}
              >
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {permitDate(p) ?? '—'}
                  {isStatusDated(p) ? (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      title={STATUS_DATE_HELP}
                      sx={{ display: 'block' }}
                    >
                      {STATUS_DATE_LABEL}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell sx={{ fontFamily: MONO, fontSize: 13, whiteSpace: 'nowrap' }}>
                  {p.source_permit_number ?? '—'}
                </TableCell>
                <TableCell>
                  {typeLabel(p)}
                  <NonBuildingBadge permit={p} />
                </TableCell>
                <TableCell sx={{ maxWidth: 320 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {p.description ?? '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {customerTags(p.tags)
                      .slice(0, MAX_TAG_CHIPS)
                      .map((t) => (
                        <TagChip key={t} tag={t} />
                      ))}
                    {customerTags(p.tags).length > MAX_TAG_CHIPS ? (
                      <MoreTagsChip
                        hidden={customerTags(p.tags).slice(MAX_TAG_CHIPS)}
                        onOpen={() => setActive(p)}
                      />
                    ) : null}
                  </Box>
                </TableCell>
                <TableCell>{scopeLabel(p.permit_scope)}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{p.final_date ?? '—'}</TableCell>
                {showAddress ? (
                  <TableCell>
                    {[p.address_line1, p.city, p.state].filter(Boolean).join(', ') || '—'}
                  </TableCell>
                ) : null}
                {isAdmin ? <TableCell>{p.source_id}</TableCell> : null}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={shown.length}
        page={page}
        rowsPerPage={PER_PAGE}
        rowsPerPageOptions={[PER_PAGE]}
        onPageChange={(_, n) => setPage(n)}
      />
      <PermitDetailModal permit={active} onClose={() => setActive(null)} />
    </>
  );
}
